// Deterministic store-locator branch slugs — the embed-side copy of the API's
// api/src/services/branchSlug.ts. The two builds can't share a module, so the
// algorithm is duplicated: KEEP THEM IN SYNC or the deep-link slug on a card
// won't match the canonical URL /api/seo emits for that branch.

/** "Acornhoek" → "acornhoek", "George (Cape)" → "george-cape". */
export function branchSlug(name) {
  return String(name == null ? '' : name)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Build a `slug → branch` object from a branch list, using the SAME stable order
 * + collision handling as the API (name, then shortCode; taken slug gets the
 * shortCode appended). Returns a plain object so callers can also derive a
 * name→slug lookup for matching store-locator cards by their data-name.
 */
export function buildBranchSlugMap(branches) {
  var map = {};
  var list = (branches || []).slice().sort(function (a, b) {
    var an = String((a && a.branchName) || '').toLowerCase();
    var bn = String((b && b.branchName) || '').toLowerCase();
    if (an !== bn) return an < bn ? -1 : 1;
    return String((a && a.branchShortCode) || '').localeCompare(String((b && b.branchShortCode) || ''));
  });
  for (var i = 0; i < list.length; i++) {
    var b = list[i];
    var base = branchSlug(String((b && b.branchName) || ''));
    if (!base) continue;
    var slug = base;
    if (Object.prototype.hasOwnProperty.call(map, slug)) {
      var code = branchSlug(String((b && (b.branchShortCode || b.branchCode)) || ''));
      slug = code ? base + '-' + code : base + '-2';
      var n = 2;
      while (Object.prototype.hasOwnProperty.call(map, slug)) slug = base + '-' + (code || 'x') + '-' + (n++);
    }
    map[slug] = b;
  }
  return map;
}

// Alternative country slugs that should land on the same country view.
var COUNTRY_ALIASES = { eswatini: 'swaziland', swaziland: 'eswatini' };

/**
 * Build a `countrySlug → { name, branches }` object (/store-locator/namibia).
 * Country slugs never shadow a branch slug: callers check the branch map first.
 * Aliases (eswatini/swaziland) point at the same entry.
 */
export function buildCountryMap(branches) {
  var map = {};
  (branches || []).forEach(function (b) {
    var name = String((b && b.country) || '').trim();
    var slug = branchSlug(name);
    if (!slug) return;
    if (!map[slug]) map[slug] = { slug: slug, name: name, branches: [] };
    map[slug].branches.push(b);
  });
  Object.keys(COUNTRY_ALIASES).forEach(function (alias) {
    var target = COUNTRY_ALIASES[alias];
    if (!map[alias] && map[target]) map[alias] = map[target];
  });
  return map;
}
